import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Settings, X, RotateCcw, Plus, Check } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ExclusionSettings() {
  const [excluded, setExcluded] = useState([]);
  const [available, setAvailable] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [customType, setCustomType] = useState("");

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings/excluded-types`);
      setExcluded(res.data.excluded || []);
      setAvailable(res.data.available || []);
      setDefaults(res.data.defaults || []);
    } catch {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async (newExcluded) => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/excluded-types`, { types: newExcluded });
      setExcluded(newExcluded);
      toast.success("Exclusion settings saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    try {
      const res = await axios.post(`${API}/settings/excluded-types/reset`);
      setExcluded(res.data.types || []);
      toast.success("Reset to defaults");
    } catch {
      toast.error("Failed to reset");
    } finally {
      setSaving(false);
    }
  };

  const toggleType = (type) => {
    const newList = excluded.includes(type)
      ? excluded.filter((t) => t !== type)
      : [...excluded, type];
    handleSave(newList);
  };

  const addCustom = () => {
    const trimmed = customType.trim();
    if (trimmed && !excluded.includes(trimmed)) {
      handleSave([...excluded, trimmed]);
      setCustomType("");
    }
  };

  const removeType = (type) => {
    handleSave(excluded.filter((t) => t !== type));
  };

  // Merge available types from DB with defaults so user sees everything
  const allKnownTypes = [...new Set([...available, ...defaults])].sort();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="exclusion-settings">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="settings-title">
          Voucher Type Exclusions
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Excluded voucher types are hidden from all financial reports and GL dump
        </p>
      </div>

      {/* Currently excluded */}
      <Card className="border border-slate-200 shadow-none mb-6">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Currently Excluded ({excluded.length})
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={handleReset}
              disabled={saving}
              className="text-xs h-7"
              data-testid="reset-defaults-btn"
            >
              <RotateCcw className="w-3 h-3 mr-1" /> Reset to Defaults
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {excluded.length === 0 ? (
            <p className="text-sm text-slate-500">No voucher types excluded. All types will appear in reports.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {excluded.map((type) => (
                <span
                  key={type}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 rounded text-sm font-medium border border-red-200"
                >
                  {type}
                  <button
                    onClick={() => removeType(type)}
                    className="hover:bg-red-100 rounded p-0.5"
                    data-testid={`remove-${type.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Available types from data */}
      <Card className="border border-slate-200 shadow-none mb-6">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700">
            Available Voucher Types (from uploaded data)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {allKnownTypes.length === 0 ? (
            <p className="text-sm text-slate-500">No voucher data uploaded yet.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {allKnownTypes.map((type) => {
                const isExcluded = excluded.includes(type);
                return (
                  <button
                    key={type}
                    onClick={() => toggleType(type)}
                    disabled={saving}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-sm font-medium border transition-colors ${
                      isExcluded
                        ? "bg-red-50 text-red-700 border-red-200 hover:bg-red-100"
                        : "bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                    }`}
                    data-testid={`toggle-${type.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {isExcluded ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                    {type}
                  </button>
                );
              })}
            </div>
          )}
          <p className="text-xs text-slate-400 mt-3">
            Click to toggle. Red = excluded from reports. Green = included.
          </p>
        </CardContent>
      </Card>

      {/* Add custom type */}
      <Card className="border border-slate-200 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700">
            Add Custom Exclusion
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={customType}
              onChange={(e) => setCustomType(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCustom()}
              placeholder="Type voucher type name..."
              className="flex-1 max-w-sm px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
              data-testid="custom-type-input"
            />
            <Button
              size="sm"
              onClick={addCustom}
              disabled={!customType.trim() || saving}
              data-testid="add-custom-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
